import { useState } from 'react'

import { useForm } from 'react-hook-form'
import { HiArrowLeft } from 'react-icons/hi'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
} from '@chakra-ui/react'

import { Button, FormInputSelectControl } from '@/components/ui'
import {
  useFacilityNamesQuery,
  useStandardOperatingProceduresFilterDataQuery,
} from '@/graphql/generated/hooks'
import {
  selectSOPCreatedAtFilter,
  selectSOPCreatorIdFilter,
  selectSOPFacilityIdFilter,
  selectSOPTypeFilter,
  setSOPCreatedAtFilter,
  setSOPCreatorIdFilter,
  setSOPFacilityIdFilter,
  setSOPTypeFilter,
} from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'
import { createdAtOptions } from '@/utils/filterDrawers'
import {
  getFacilityOptions,
  getSOPCreatorOptions,
  getSOPTypeOptions,
} from '@/utils/forms/optionBuilders'
import {
  MIN_CHAR_COUNT,
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

export const SOPsFilterDrawer = ({ isOpen, onClose }: IProps) => {
  const dispatch = useDispatch()
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )
  const { data } = useStandardOperatingProceduresFilterDataQuery({
    fetchPolicy: 'network-only',
  })
  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityNamesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter:
          debouncedfacilityName?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilityName }
            : undefined,
      },
    })
  const { control, handleSubmit, setValue } = useForm({
    reValidateMode: 'onChange',
  })
  const idsFilterValue = useSelector(selectSOPFacilityIdFilter)
  const createdAtFilterValue = useSelector(selectSOPCreatedAtFilter)
  const creatorFilterValue = useSelector(selectSOPCreatorIdFilter)
  const typeValue = useSelector(selectSOPTypeFilter)
  const facilityOptions = getFacilityOptions(facilitiesData)
  const creatorOptions = getSOPCreatorOptions(data)
  const typeOptions = getSOPTypeOptions()
  const onClearFilters = () => {
    setValue('name', null)
    setValue('createdAt', null)
    setValue('creator', null)
    setValue('type', null)

    mixpanel.track('Button Press: SOPs Drawer - Clear Filters')
  }

  const onSubmit = async (values) => {
    const selectedFacilityId = values?.name
    const selectedCreatedAtRange = values?.createdAt
    const selectedCreator = values?.creator
    const selectedType = values?.type
    dispatch(setSOPFacilityIdFilter(selectedFacilityId || null))
    dispatch(setSOPCreatedAtFilter(selectedCreatedAtRange || null))
    dispatch(setSOPCreatorIdFilter(selectedCreator || null))
    dispatch(setSOPTypeFilter(selectedType || null))
    onClose()
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='sm'>
      <DrawerOverlay data-testid='SOPsFilterDrawer:overlay' />
      <DrawerContent>
        <DrawerHeader
          alignItems='center'
          borderBottomWidth='1px'
          borderColor='#D5DCE4'
          d='flex'
          minH='65px'
          px='5'
          py='0'
        >
          <HStack spacing='3'>
            <IconButton
              aria-label='Back button'
              borderRadius='4px'
              data-testid='sopsPage_filterDrawer_backButton'
              h='30px'
              icon={<HiArrowLeft size={18} />}
              minWidth='30px'
              onClick={onClose}
              variant='ghost'
              w='30px'
            />
            <Box fontSize='20px' fontWeight='bold' letterSpacing='-0.53px'>
              Filter SOPs
            </Box>
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          <Box mb='3' mt='6'>
            <FormInputSelectControl
              closeMenuOnSelect={true}
              control={control}
              dataTestId='sopsPage_filterDrawer_facilityName'
              defaultValue={idsFilterValue || null}
              id='name'
              isClearable
              isLoading={isFacilitiesLoading}
              isSearchable
              label='Facility Name'
              noOptionsMessage={({ inputValue }) =>
                getFacilityNoOptionsMessage(inputValue)
              }
              onInputChange={setFacilityName}
              options={facilityOptions}
              placeholder='Select Facility'
            />
          </Box>
          <Box mb='3'>
            <FormInputSelectControl
              control={control}
              dataTestId='sopsPage_filterDrawer_createdAt'
              defaultValue={createdAtFilterValue || null}
              id='createdAt'
              isClearable
              isSearchable={false}
              label='Created At'
              options={createdAtOptions}
              placeholder='Select Time Range'
            />
          </Box>
          <Box mb='3'>
            <FormInputSelectControl
              control={control}
              dataTestId='sopsPage_filterDrawer_creator'
              defaultValue={creatorFilterValue || null}
              id='creator'
              isClearable
              isSearchable={false}
              label='Creator'
              options={creatorOptions}
              placeholder='Select Creator'
            />
          </Box>
          <Box mb='3'>
            <FormInputSelectControl
              control={control}
              dataTestId='sopsPage_filterDrawer_type'
              defaultValue={typeValue || null}
              id='type'
              isClearable
              isSearchable={false}
              label='Operator/Guard'
              options={typeOptions}
              placeholder='Select Option'
            />
          </Box>
        </DrawerBody>
        <DrawerFooter borderColor='#D5DCE4' borderTopWidth='1px'>
          <HStack spacing='5' width='full'>
            <Button
              data-testid='sopsPage_filterDrawer_clearButton'
              onClick={onClearFilters}
              variant='secondary'
              width='full'
            >
              Clear All
            </Button>
            <Button
              data-testid='sopsPage_filterDrawer_applyButton'
              onClick={handleSubmit(onSubmit)}
              width='full'
            >
              Apply Filters
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
